/*
 * Jira Ticket:
 * Created Date: Tue, 17th Mar 2020, 14:12:17 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import {
  BooleanInput,
  Edit,
  NumberInput,
  required,
  SimpleForm,
  TextInput,
  SelectInput,
  Title,
  useDataProvider,
  fetchStart,
  fetchEnd,
  useNotify,
} from 'react-admin';
import { AccessDenied } from 'Layout';
import { SaveDeleteToolbar } from 'Components';
import { columnStyles } from 'styles';
import { isManager, isProjectManager } from 'utils/roles';
import { checklistTypes } from 'utils/choices';
import * as R from 'ramda';
import { areChecklistTypesExtended } from 'utils/capabilities';
import getOrganisationInfo from '../../utils/getOrganisationInfo';
import { useDispatch } from 'react-redux';

const SanitizedForm = ({
  classes,
  canEdit,
  hasExtendedChecklistTypesCapability,
}) => {
  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label={'resources.Project.fields.project'}
          disabled={!canEdit}
          validate={required()}
        />
        <TextInput
          source="organisation.name"
          label="Organisation"
          disabled={true}
        />
        <BooleanInput
          source="siteCreationEnabled"
          label={'resources.Project.fields.allowSites'}
          disabled={!canEdit}
        />
        <BooleanInput
          source="showTowerImages"
          label={'resources.Project.fields.allowTowerImages'}
          disabled={!canEdit}
        />
      </div>
      <div className={classes.column}>
        <NumberInput
          source="statistics.numberOfSites"
          label={'resources.Project.fields.numberOfSites'}
          disabled={true}
        />
        <NumberInput
          source="statistics.numberOfTowers"
          label={'resources.Project.fields.numberOfTowers'}
          disabled={true}
        />
        <NumberInput
          source="statistics.numberOfBuilders"
          label={'resources.Project.fields.numberOfBuilders'}
          disabled={true}
        />
        <NumberInput
          source="statistics.numberOfApprovers"
          label={'resources.Project.fields.numberOfApprovers'}
          disabled={true}
        />
        <SelectInput
          source="checklistType"
          label={'resources.Project.fields.checklistType'}
          choices={checklistTypes}
          validate={required()}
          disabled={!hasExtendedChecklistTypesCapability} // depends on capabilities??
        />
      </div>
    </div>
  );
};

const ProjectEdit = props => {
  const classes = columnStyles();
  const { permissions } = props;
  const { organisationId } = props.location.state || {};

  const [
    hasExtendedChecklistTypesCapability,
    setHasExtendedChecklistCapability,
  ] = useState(false);
  const notify = useNotify();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();

  const canEdit = organisationId
    ? isManager(permissions, organisationId) // PM can view, but not edit
    : false; // prevent editing if there's no orgId

  const redirect = `/Project`;

  // Check wether org has extended checklist types capability
  const useMountEffect = fun => useEffect(fun, []);
  useMountEffect(
    getOrganisationInfo(organisationId, {
      dataProvider,
      dispatch,
      notify,
      setOrganisationCapabilities: capabilities =>
        setHasExtendedChecklistCapability(
          areChecklistTypesExtended(capabilities)
        ),
    })
  );

  const rest = {
    classes,
    canEdit,
    hasExtendedChecklistTypesCapability,
    ...props,
  };
  return isProjectManager(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Edit {...props}>
        <SimpleForm
          redirect={redirect}
          toolbar={
            canEdit ? <SaveDeleteToolbar deleteRedirect={redirect} /> : null
          }
        >
          <SanitizedForm {...rest} />
        </SimpleForm>
      </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

export default ProjectEdit;
